import { getLocale, t as tr } from "@/lib/i18n";
import { ConnectionsHost } from "@fox1ck-org/auth/ui";
import { PRODUCT } from "@/lib/auth";
import { useMemo, useState } from "react";
import { Plug, ShieldCheck } from "lucide-react";

import { Skeleton } from "@fox1ck-org/ui";
import {
  categoryLabel,
  PROVIDER_META,
  providerFromWireName,
  useConnectionsCatalog,
  type ProviderCategory,
} from "@/lib/services/integration-service";
import { IntegrationProvider } from "@gen/tracker/v1/integration_pb";
import { useTrackerRoles } from "@/lib/permissions";

import { ProviderCard } from "./provider-card";
import { ConnectionSheet, type SheetTarget } from "./connection-sheet";
import { useDeepLinkParam } from "@/lib/hooks/use-deep-link-param";
import { PageState } from "@fox1ck-org/ui";
import { failureText } from "@/lib/errors";

const CATEGORY_ORDER: ProviderCategory[] = ["dns", "ai"];

// ConnectionsPage is «Подключения»: first the tracker's ports as console
// resolves them (`ConnectionsHost`, spec blocks §6 — port `domains`: which
// block or service attaches the tracker's domains, by edition), then the
// provider catalog: one card per supported provider
// (Cloudflare / OpenRouter — registrars are the domains port's), each showing whether the
// workspace runs on its own connection, the platform default, or nothing.
// Credentials live encrypted in the DB and are managed entirely here —
// there is no env/K8s configuration path.
export default function ConnectionsPage() {
  const { data: catalog, isLoading, error } = useConnectionsCatalog();
  const { isPlatformAdmin } = useTrackerRoles();

  const [sheetTarget, setSheetTarget] = useState<SheetTarget | null>(null);

  // /connections?setup=PORKBUN deep link (from connection_required errors)
  // auto-opens the setup sheet for that provider.
  useDeepLinkParam("setup", (raw) => {
    const provider = providerFromWireName(raw);
    if (provider !== null) setSheetTarget({ provider, scope: "workspace" });
  });

  const sections = useMemo(
    () =>
      CATEGORY_ORDER.map((category) => ({
        category,
        providers: PROVIDER_META.filter((m) => m.category === category),
      })),
    [],
  );

  const connectedCount = useMemo(() => {
    if (!catalog) return 0;
    return PROVIDER_META.filter((m) => {
      const slot = catalog[m.provider];
      return slot?.workspace || slot?.platform;
    }).length;
  }, [catalog]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
      <div className="flex flex-col gap-4">

      <ConnectionsHost product={PRODUCT} locale={getLocale()} className="pt-1" />

      <header className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Plug className="h-4 w-4" strokeWidth={1.75} />
              <span className="text-xs font-semibold uppercase tracking-[0.08em]">
                {tr("tpl.scope.ws")}
              </span>
            </div>
            <h1 className="mt-2 text-page-title text-foreground">
              {tr("conn.title")}
            </h1>
            <p className="mt-1 max-w-160 text-sm leading-relaxed text-muted-foreground">
              {tr("conn.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card/70 px-3 py-1.5 backdrop-blur">
              <span
                className={
                  "inline-block h-2 w-2 rounded-full " +
                  (connectedCount === PROVIDER_META.length ? "bg-ok" : "bg-warn")
                }
              />
              <span className="text-xs tabular-nums text-foreground">
                {tr("conn.count", { n: connectedCount, total: PROVIDER_META.length })}
              </span>
            </div>
            {isPlatformAdmin ? (
              <div
                className="flex items-center gap-1.5 rounded-full border border-border bg-card/70 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur"
                title={tr("conn.admin.hint")}
              >
                <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                {tr("conn.admin")}
              </div>
            ) : null}
          </div>
        </div>
      </header>

      {error ? (
        <PageState mode="error" title={tr("conn.loadError")} error={failureText(error)} />
      ) : null}

      {isLoading && !catalog ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-44 w-full rounded-2xl" />
          ))}
        </div>
      ) : (
        sections.map(({ category, providers }) => (
          <section key={category} className="space-y-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                {categoryLabel(category)}
              </h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {providers.map((meta) => (
                <ProviderCard
                  key={meta.provider}
                  meta={meta}
                  connections={catalog?.[meta.provider] ?? {}}
                  isPlatformAdmin={isPlatformAdmin}
                  onConnect={(scope) => setSheetTarget({ provider: meta.provider, scope })}
                  onEdit={(scope) =>
                    setSheetTarget({ provider: meta.provider, scope, editing: true })
                  }
                />
              ))}
            </div>
          </section>
        ))
      )}

      <ConnectionSheet
        target={sheetTarget}
        existing={
          sheetTarget
            ? sheetTarget.scope === "platform"
              ? catalog?.[sheetTarget.provider]?.platform
              : catalog?.[sheetTarget.provider]?.workspace
            : undefined
        }
        onOpenChange={(open) => {
          if (!open) setSheetTarget(null);
        }}
      />
      </div>
      </div>
    </div>
  );
}

// Re-exported so deep links elsewhere can build ?setup= URLs without
// importing the service module directly.
export type { SheetTarget };
export { IntegrationProvider };
